import type { FogLevel, StationReading } from "./shared";

// Shared fog-score normalization. Every source (METAR, TAF, Open-Meteo,
// HRRR, GLMP) saturates at the same physical thresholds so the blended
// sub-scores compare like with like.

export const METERS_PER_MILE = 1609.344;
export const METERS_PER_FOOT = 0.3048;

// Visibility at or above 8 statute miles reads as fully clear.
export const VISIBILITY_CLEAR_MILES = 8;
const VISIBILITY_CLEAR_METERS = VISIBILITY_CLEAR_MILES * METERS_PER_MILE;

// Ceiling at or above 2,000 ft reads as fully clear.
export const CEILING_CLEAR_FEET = 2000;
const CEILING_CLEAR_METERS = CEILING_CLEAR_FEET * METERS_PER_FOOT;

export function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

export function scoreToLevel(score: number): FogLevel {
  if (score >= 0.55) return "dense";
  if (score >= 0.30) return "patchy";
  if (score >= 0.12) return "thin";
  return "clear";
}

export function visibilityScoreFromMiles(miles: number): number {
  return clamp((VISIBILITY_CLEAR_MILES - miles) / VISIBILITY_CLEAR_MILES);
}

export function visibilityScoreFromMeters(meters: number): number {
  return clamp((VISIBILITY_CLEAR_METERS - meters) / VISIBILITY_CLEAR_METERS);
}

export function ceilingScoreFromFeet(feet: number): number {
  return clamp((CEILING_CLEAR_FEET - feet) / CEILING_CLEAR_FEET);
}

export function ceilingScoreFromMeters(meters: number): number {
  return clamp((CEILING_CLEAR_METERS - meters) / CEILING_CLEAR_METERS);
}

// Single METAR scorer used by both the live grid and observed history so the
// same observation can't produce two different scores.
export function stationFogScore(station: StationReading): number {
  const visibility = station.visibilityMiles ?? 10;
  const ceiling = station.ceilingFeet ?? 5000;
  const dewSpread =
    station.temperatureF !== null && station.dewpointF !== null
      ? Math.max(0, station.temperatureF - station.dewpointF)
      : 8;
  const visScore = visibilityScoreFromMiles(visibility);
  const ceilingScore = ceilingScoreFromFeet(ceiling);
  const dewScore = clamp((8 - dewSpread) / 8);
  const fogTextScore = station.raw?.includes(" FG ") ? 0.16 : station.raw?.includes(" BR ") ? 0.08 : 0;
  return clamp(visScore * 0.42 + ceilingScore * 0.36 + dewScore * 0.14 + fogTextScore);
}

// Display-only estimates derived from the blended score; most cells have no
// direct visibility/ceiling measurement.
export function syntheticVisibilityMiles(score: number): number {
  return Math.max(0.2, Math.round((10 - score * 9.6) * 10) / 10);
}

export function syntheticCeilingFeet(score: number): number {
  return Math.round((3200 - score * 2850) / 50) * 50;
}
